import { UserCoverageAndSelections } from '../use-coverage';
import { CoverageScreenType } from './coverage-type';
import { getTitle } from './title';
import { Category, SubCategory } from '../../../constants';
import { aOrAn } from '../../../utilities';

const MAX_LENGTH = 2000;

type Deductible = {
  inNetwork?: number;
  outOfNetwork?: number;
};

const getDescriptionPlain = (
  coverageScreenType: CoverageScreenType,
  make: string,
  userCoverageAndSelections: UserCoverageAndSelections,
  deductible?: Deductible,
  daysLeft?: number,
  carvanaCare?: string | null
): string[] => {
  const safeMake = make || "your vehicle's manufacturer";
  const inNetwork = deductible?.inNetwork ?? 100;
  const outOfNetwork = deductible?.outOfNetwork ?? 350;
  const deductibleDiff = outOfNetwork - inNetwork;
  const hasDeductibleDiff = outOfNetwork !== inNetwork;

  const coveredWithDeductible = (): string[] => {
    const lines = [
      "Schedule an appointment with an in-network repair shop to diagnose your issue. They'll work with our coverage provider, SilverRock, to confirm your repairs are covered.",
      `In-network deductible: $${inNetwork}`,
    ];
    if (hasDeductibleDiff) {
      lines.push(`Save $${deductibleDiff} on your deductible by choosing an in-network shop.`);
    }
    return lines;
  };

  switch (coverageScreenType) {
    case CoverageScreenType.contactUs:
      return ['One of our Advocates may be able to resolve your issue.'];

    case CoverageScreenType.limited100Covered:
    case CoverageScreenType.carvanaCareCovered:
    case CoverageScreenType.carvanaCareDentCovered:
      return coveredWithDeductible();

    case CoverageScreenType.manufacturerCovered:
      return [`Schedule an appointment with ${aOrAn(safeMake)} ${safeMake} repair center to get a diagnostic.`];

    case CoverageScreenType.carvanaCareRoadHazardCovered:
      return [
        "Schedule an appointment with an in-network repair shop to assess your damage. They'll work with our coverage provider, SilverRock, to confirm your damage is covered.",
      ];

    case CoverageScreenType.carvanaCareRoadHazardNotCovered:
      return [
        `Your ${carvanaCare || 'CarvanaCare'} coverage only covers damage caused by road debris, a curb or pothole, otherwise known as "road hazard" damage. It does not cover normal wear or tear.`,
        'Get service at a recommended shop or find one that works for you.',
      ];

    case CoverageScreenType.carvanaCareOilChangeCovered:
      return [
        "Service must be done at an in-network shop for coverage to apply. Schedule an appointment with the shop and they'll work with our coverage provider, SilverRock, to process the repair.",
      ];

    case CoverageScreenType.carvanaCareOilChangeNotCovered:
      return [
        'Maintenance like oil changes, tire rotations, wiper blade and fluid replacements are excluded.',
        'Get service at a recommended shop or find one that works for you.',
      ];

    case CoverageScreenType.carvanaCareOilTireCovered: {
      const { subcategory, subsubcategory } = userCoverageAndSelections;
      if (
        (subcategory === SubCategory.Tires || subcategory === SubCategory.Wheels) &&
        subsubcategory === 'Yes'
      ) {
        return [
          "Schedule an appointment with an in-network repair shop to assess your damage. They'll work with our coverage provider, SilverRock, to confirm your damage is covered.",
        ];
      }
      return [
        "Service must be done at an in-network shop for coverage to apply. Schedule an appointment with the shop and they'll work with our coverage provider, SilverRock, to process the repair.",
      ];
    }

    case CoverageScreenType.thirtyDayCovered: {
      const { subcategory } = userCoverageAndSelections;
      const lines: string[] = [];

      if (subcategory === SubCategory.Brakes) {
        lines.push("Brake issues are only covered if they're safety related, such as perforated rust.");
      } else if (subcategory === SubCategory.Tires) {
        lines.push("Tire issues are only covered if they're safety related, such as treadless tire.");
      } else if (subcategory === SubCategory.Wheels) {
        lines.push("Wheel issues are only covered if they're safety related, such as cracked or bent rim.");
      }

      if (daysLeft != null && daysLeft > 0) {
        const expirationDate = new Date(new Date().setDate(new Date().getDate() + daysLeft));
        const dateStr = expirationDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        lines.push(`Schedule an appointment with an in-network repair shop to diagnose your issue by ${dateStr}.`);
        lines.push("They'll work with our coverage provider, SilverRock, to confirm your repairs are covered.");
        lines.push(`In-network deductible: $${inNetwork}`);
        if (hasDeductibleDiff) {
          lines.push(`Save $${deductibleDiff} on your deductible by choosing an in-network shop.`);
        }
      } else {
        lines.push(...coveredWithDeductible());
      }

      return lines;
    }

    case CoverageScreenType.conditionallyCoveredBrakes: {
      const deductibleText = hasDeductibleDiff
        ? `Save $${deductibleDiff} on your deductible by using an in-network shop for covered repairs.`
        : `There's a $${inNetwork} deductible by using an in-network shop for covered repairs.`;
      return [
        'Wearable components such as brake pads and rotors are excluded for your coverage.',
        `Schedule an appointment with an in-network repair shop to diagnose your issue. ${deductibleText}`,
        "They'll work with our coverage provider, SilverRock, to verify your issue and make a determination.",
      ];
    }

    case CoverageScreenType.notCovered: {
      const { category } = userCoverageAndSelections;
      if (category === Category.Battery || category === Category.TiresOrBrakes) {
        return [
          'Wearable components like tires, brakes or batteries are excluded.',
          'Get service at a recommended shop or find one that works for you.',
        ];
      }
      if (category === Category.Maintenance) {
        return [
          'Maintenance like oil changes, tire rotations, wiper blade and fluid replacements are excluded.',
          'Get service at a recommended shop or find one that works for you.',
        ];
      }
      return ['Get service at a recommended shop or find one that works for you.'];
    }

    case CoverageScreenType.notCoveredCosmetic:
      return ['Get service at a recommended shop or find one that works for you.'];

    case CoverageScreenType.notCoveredGlass:
      return ['Get service at the recommended business or find one that works for you.'];

    case CoverageScreenType.notCoveredAdditionalCosmetic:
      return [
        "You've submitted the max amount of cosmetic concerns.",
        'Get service at a recommended shop or find one that works for you.',
      ];

    case CoverageScreenType.notCoveredAdditionalKey:
      return ['Get service at the recommended business.'];

    case CoverageScreenType.notCoveredMissingAccessories:
      return [`We recommend visiting ${safeMake}'s website or an auto-parts store to replace any missing accessories.`];

    case CoverageScreenType.cosmeticSubmitted:
      return ['We will email you within 48 hours with next steps.'];

    default:
      return ['One of our Advocates may be able to resolve your issue.'];
  }
};

export const getCoverageTextPlain = (
  coverageScreenType: CoverageScreenType,
  userCoverageAndSelections: UserCoverageAndSelections,
  make: string,
  deductible?: Deductible,
  daysLeft?: number,
  carvanaCare?: string | null
): string => {
  const descriptionLines = getDescriptionPlain(coverageScreenType, make, userCoverageAndSelections, deductible, daysLeft, carvanaCare);

  const skipTitle = coverageScreenType === CoverageScreenType.thirtyDayCovered;
  const lines = skipTitle
    ? descriptionLines
    : [getTitle(coverageScreenType, userCoverageAndSelections), ...descriptionLines];

  const fullText = lines.join('\n');

  if (fullText.length > MAX_LENGTH) {
    return fullText.substring(0, MAX_LENGTH);
  }

  return fullText;
};
